<template>
  <div class="scout-public">
    <!-- ══ HEADER ══ -->
    <div class="hdr-bar">
      <div class="hdr-left">
        <div class="hdr-icon-wrap">
          <v-icon size="28" color="white">mdi-tent</v-icon>
        </div>
        <div>
          <div class="hdr-title">สถิติข้อมูลลูกเสือวิสามัญอาชีวศึกษา</div>
          <div class="hdr-sub">
            ข้อมูลเชิงสถิติเพื่อบริการข้อมูลสาธารณะ สังกัดสำนักงานคณะกรรมการการอาชีวศึกษา
          </div>
        </div>
      </div>
      <div class="hdr-right">
        <div class="hdr-pill">
          <v-progress-circular
            v-if="loading"
            indeterminate
            color="white"
            size="14"
            width="2"
          ></v-progress-circular>
          <template v-else>
            <v-icon size="14">mdi-account-group</v-icon>
            <strong>{{ maskCount(totalAll) }}</strong>&nbsp;คน
          </template>
        </div>
      </div>
    </div>
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="#2e7d32"
      height="3"
      class="loading-bar"
    ></v-progress-linear>

    <v-container fluid class="py-4">  

      <!-- ══ FILTER BAR (ภาค/จังหวัด/ระดับ เท่านั้น — ไม่มีชื่อ/วิทยาลัยรายบุคคล) ══ -->
      <v-card class="filter-card mb-4" outlined>
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="filters.filter_region_ID"
              label="ภาค"
              :items="region"
              item-text="region_name"
              item-value="region_ID"
              clearable
              dense
              hide-details
              outlined
              :disabled="loading"
              @change="onRegionChange"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-autocomplete
              v-model="filters.filter_province_ID"
              label="จังหวัด"
              :items="filteredProvince"
              item-text="province_name"
              item-value="province_ID"
              clearable
              dense
              hide-details
              outlined
              :disabled="loading"
              @change="loadStats"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="filters.scout_level"
              label="ระดับลูกเสือ"
              :items="scout_levels"
              clearable
              dense
              hide-details
              outlined
              :disabled="loading"
              @change="loadStats"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3" class="text-md-right">
            <v-btn class="btn-export" :loading="exporting" :disabled="loading" @click="exportStats">
              <v-icon left small>mdi-file-excel</v-icon>ส่งออกสถิติ (xlsx)
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <!-- ══ สรุปตัวเลข ══ -->
      <v-row class="mb-2">
        <v-col cols="12" sm="6">
          <v-card class="stat-card stat-card-1" elevation="0">
            <div class="stat-icon"><v-icon color="white" size="26">mdi-account-group</v-icon></div>
            <div class="stat-content">
              <v-progress-circular
                v-if="loading"
                indeterminate
                color="white"
                size="20"
                width="2"
              ></v-progress-circular>
              <span v-else class="stat-number">{{ maskCount(totalAll) }}</span>
              <span class="stat-label">บุคลากรด้านลูกเสือทั้งหมด (ตามตัวกรอง)</span>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6">
          <v-card class="stat-card stat-card-2" elevation="0">
            <div class="stat-icon"><v-icon color="white" size="26">mdi-account-star</v-icon></div>
            <div class="stat-content">
              <v-progress-circular
                v-if="loading"
                indeterminate
                color="white"
                size="20"
                width="2"
              ></v-progress-circular>
              <span v-else class="stat-number">{{ maskCount(instructorTotal) }}</span>
              <span class="stat-label">วิทยากรลูกเสือ</span>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ══ กราฟ (จัดกลุ่มย่อยที่น้อยกว่า 5 คน เป็น "อื่นๆ" แล้ว) ══ -->
      <v-row>
        <v-col cols="12" md="5">
          <v-card class="pa-3 chart-card" outlined>
            <div class="subtitle-1 font-weight-bold mb-2">สัดส่วนตามระดับลูกเสือ</div>
            <apexchart
              v-if="chartByLevel.series.length"
              type="donut"
              height="300"
              :options="chartByLevel.options"
              :series="chartByLevel.series"
            ></apexchart>
            <div v-else-if="loading" class="chart-loading py-10">
              <v-progress-circular indeterminate color="#2e7d32" size="32" width="3"></v-progress-circular>
              <span class="mt-2">กำลังโหลดข้อมูล...</span>
            </div>
            <div v-else class="text-center grey--text py-10">ไม่มีข้อมูลตามตัวกรองนี้</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="7">
          <v-card class="pa-3 chart-card" outlined>
            <div class="subtitle-1 font-weight-bold mb-2">จำนวนตามตำแหน่งทางลูกเสือ</div>
            <apexchart
              v-if="chartByPosition.series[0] && chartByPosition.series[0].data.length"
              type="bar"
              height="300"
              :options="chartByPosition.options"
              :series="chartByPosition.series"
            ></apexchart>
            <div v-else-if="loading" class="chart-loading py-10">
              <v-progress-circular indeterminate color="#2e7d32" size="32" width="3"></v-progress-circular>
              <span class="mt-2">กำลังโหลดข้อมูล...</span>
            </div>
            <div v-else class="text-center grey--text py-10">ไม่มีข้อมูลตามตัวกรองนี้</div>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card class="pa-3 chart-card" outlined>
            <div class="subtitle-1 font-weight-bold mb-2">จำนวนบุคลากรด้านลูกเสือตามภาค</div>
            <apexchart
              v-if="chartByRegion.series[0] && chartByRegion.series[0].data.length"
              type="bar"
              height="300"
              :options="chartByRegion.options"
              :series="chartByRegion.series"
            ></apexchart>
            <div v-else-if="loading" class="chart-loading py-10">
              <v-progress-circular indeterminate color="#2e7d32" size="32" width="3"></v-progress-circular>
              <span class="mt-2">กำลังโหลดข้อมูล...</span>
            </div>
            <div v-else class="text-center grey--text py-10">ไม่มีข้อมูลตามตัวกรองนี้</div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-snackbar v-model="snackbar.show" :timeout="4000" :color="snackbar.color" top right>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import VueApexCharts from "vue-apexcharts";

// จำนวนขั้นต่ำต่อหมวดหมู่ก่อนจะถูกรวมเป็น "อื่นๆ" — ป้องกันการระบุตัวบุคคลทางอ้อม
// จากหมวดหมู่ที่มีคนน้อยมาก (k-anonymity แบบง่าย) ตามหลัก PDPA
const K_THRESHOLD = 5;

export default {
  name: "PersonnelScoutReportPublic",
  components: { apexchart: VueApexCharts },

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      exporting: false,
      region: [],
      province: [],
      scout_levels: [
        "ลูกเสือสำรอง",
        "ลูกเสือสามัญ",
        "ลูกเสือสามัญรุ่นใหญ่",
        "ลูกเสือวิสามัญ"
      ],

      // หมายเหตุ: ตั้งใจไม่มี college_code / search ในหน้าสาธารณะนี้ — การกรองแคบถึงระดับ
      // วิทยาลัย/ชื่อบุคคล อาจทำให้กลุ่มตัวอย่างเล็กจนระบุตัวบุคคลได้ทางอ้อม
      filters: {
        filter_region_ID: null,
        filter_province_ID: null,
        scout_level: null
      },

      totalAll: 0,
      instructorTotal: 0,
      groupedByLevel: [],
      groupedByPosition: [],
      groupedByRegion: [],

      chartByLevel: { series: [], options: {} },
      chartByPosition: { series: [{ name: "จำนวน", data: [] }], options: {} },
      chartByRegion: { series: [{ name: "จำนวน", data: [] }], options: {} },

      snackbar: { show: false, color: "", text: "" }
    };
  },

  computed: {
    filteredProvince() {
      if (!this.filters.filter_region_ID) return this.province;
      return this.province.filter(p => p.region_ID === this.filters.filter_region_ID);
    }
  },

  async mounted() {
    await this.loadRegionProvince();
    await this.loadStats();
  },

  methods: {
    onRegionChange() {
      this.filters.filter_province_ID = null;
      this.loadStats();
    },

    async loadRegionProvince() {
      let [regionRes, provinceRes] = await Promise.all([
        this.$http.post("region.php", { ApiKey: this.ApiKey }),
        this.$http.post("province.php", { ApiKey: this.ApiKey })
      ]);
      this.region = regionRes.data;
      this.province = provinceRes.data;
    },

    // รวมหมวดหมู่ที่มีจำนวนน้อยกว่าเกณฑ์ (K_THRESHOLD) เป็น "อื่นๆ" ก่อนแสดงผล/ส่งออกเสมอ
    groupSmallCategories(rows) {
      const big = [];
      let smallSum = 0;
      (rows || []).forEach(r => {
        const n = Number(r.total) || 0;
        if (n >= K_THRESHOLD) {
          big.push({ label: r.label || "ไม่ระบุ", total: n });
        } else {
          smallSum += n;
        }
      });
      if (smallSum > 0) {
        big.push({ label: "อื่นๆ (กลุ่มย่อยรวมกัน)", total: smallSum });
      }
      return big;
    },

    maskCount(n) {
      const num = Number(n) || 0;
      if (num > 0 && num < K_THRESHOLD) return `น้อยกว่า ${K_THRESHOLD}`;
      return num.toLocaleString("th-TH");
    },

    async loadStats() {
      this.loading = true;
      try {
        let result = await this.$http.post("personnel_scout_report.php", {
          ApiKey: this.ApiKey,
          action: "stats",
          filter_region_ID: this.filters.filter_region_ID,
          filter_province_ID: this.filters.filter_province_ID,
          scout_level: this.filters.scout_level
        });
        const d = result.data;

        this.totalAll = (d.by_level || []).reduce((s, r) => s + Number(r.total), 0);
        this.instructorTotal = d.instructor_total || 0;

        this.groupedByLevel = this.groupSmallCategories(d.by_level);
        this.groupedByPosition = this.groupSmallCategories(d.by_position);
        this.groupedByRegion = this.groupSmallCategories(d.by_region);

        this.chartByLevel = {
          series: this.groupedByLevel.map(r => r.total),
          options: {
            labels: this.groupedByLevel.map(r => r.label),
            legend: { position: "bottom" },
            colors: ["#B3151B", "#424242", "#E57373", "#9E9E9E", "#8E1014"],
            dataLabels: { enabled: true }
          }
        };

        this.chartByPosition = {
          series: [{ name: "จำนวน", data: this.groupedByPosition.map(r => r.total) }],
          options: {
            chart: { toolbar: { show: false } },
            xaxis: { categories: this.groupedByPosition.map(r => r.label) },
            plotOptions: { bar: { borderRadius: 4, horizontal: true } },
            colors: ["#B3151B"],
            dataLabels: { enabled: false }
          }
        };

        this.chartByRegion = {
          series: [{ name: "จำนวน", data: this.groupedByRegion.map(r => r.total) }],
          options: {
            chart: { toolbar: { show: false } },
            xaxis: { categories: this.groupedByRegion.map(r => r.label) },
            plotOptions: { bar: { borderRadius: 4, horizontal: false } },
            colors: ["#B3151B"],
            dataLabels: { enabled: false }
          }
        };
      } catch (e) {
        this.showSnackbar("error", "โหลดข้อมูลสถิติไม่สำเร็จ");
      } finally {
        this.loading = false;
      }
    },

    async exportStats() {
      this.exporting = true;
      try {
        const workbook = XLSX.utils.book_new();

        const summarySheet = XLSX.utils.json_to_sheet([
          { รายการ: "บุคลากรด้านลูกเสือทั้งหมด (ตามตัวกรอง)", จำนวน: this.maskCount(this.totalAll) },
          { รายการ: "วิทยากรลูกเสือ", จำนวน: this.maskCount(this.instructorTotal) }
        ]);
        XLSX.utils.book_append_sheet(workbook, summarySheet, "สรุป");

        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(this.groupedByLevel.map(r => ({ ระดับลูกเสือ: r.label, จำนวน: r.total }))),
          "ตามระดับ"
        );
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(this.groupedByPosition.map(r => ({ ตำแหน่งทางลูกเสือ: r.label, จำนวน: r.total }))),
          "ตามตำแหน่ง"
        );
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(this.groupedByRegion.map(r => ({ ภาค: r.label, จำนวน: r.total }))),
          "ตามภาค"
        );
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet([
            {
              หมายเหตุ:
                "ข้อมูลนี้เป็นข้อมูลเชิงสถิติ (aggregate) เท่านั้น ไม่มีชื่อหรือข้อมูลที่ระบุตัวบุคคลได้ " +
                "หมวดหมู่ที่มีจำนวนน้อยกว่า " + K_THRESHOLD + " คน ถูกรวมเป็น \"อื่นๆ\" ตามหลัก PDPA"
            }
          ]),
          "หมายเหตุ PDPA"
        );

        const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(
          new Blob([wbout], { type: "application/octet-stream" }),
          `สถิติลูกเสือวิสามัญอาชีวศึกษา_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
      } catch (e) {
        this.showSnackbar("error", "ส่งออกสถิติไม่สำเร็จ");
      } finally {
        this.exporting = false;
      }
    },

    showSnackbar(color, text) {
      this.snackbar = { show: true, color, text };
    }
  }
};
</script>

<style scoped>
.loading-bar {
  margin-bottom: 0;
}

.chart-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #757575;
  font-size: 0.85rem;
}

.hdr-bar {
  background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
  border-radius: 12px;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 4px;
}

.hdr-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.hdr-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hdr-title {
  color: #fff;
  font-size: 1.25rem;
  font-weight: 700;
}

.hdr-sub {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
}

.hdr-pill {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 6px;
}

.pdpa-notice {
  border-radius: 10px;
  font-size: 0.88rem;
  line-height: 1.6;
}

.filter-card {
  border-radius: 12px;
  padding: 16px;
}

.btn-export {
  background: linear-gradient(135deg, #2e7d32, #1b5e20) !important;
  color: #fff !important;
  border-radius: 10px;
}

.chart-card {
  border-radius: 12px;
}

.stat-card {
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #fff;
}

.stat-card-1 {
  background: linear-gradient(135deg, #2e7d32, #2e7d32);
}
.stat-card-2 {
  background: linear-gradient(135deg, #455a64, #455a64);
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 1.3rem;
  font-weight: 700;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.78rem;
  opacity: 0.9;
}
</style>
